import { z } from 'zod';

/**
 * Central de Conhecimento (ADR 0018): leitura do cabeçalho dos documentos de docs/conhecimento/
 * e entradas da API de revisão, aprovação e publicação.
 */

export const KNOWLEDGE_DEPARTMENTS = [
  'finance',
  'controlling',
  'purchasing',
  'facilities',
  'legal',
  'hr',
  'personnel',
  'it',
  'fpa',
  'administrative',
  'system',
] as const;
export type KnowledgeDepartment = (typeof KNOWLEDGE_DEPARTMENTS)[number];

/** Departamentos que podem ter documentos restritos (cada um com a sua permissão). */
export const KNOWLEDGE_RESTRICTED_DEPARTMENTS = ['finance', 'legal', 'hr', 'personnel'] as const;

export const KNOWLEDGE_DOC_TYPES = ['mestre', 'manual', 'pop', 'ito', 'faq', 'novidade'] as const;

/** Perfil que aprova perguntas frequentes e novidades. */
export const KNOWLEDGE_CENTRAL_APPROVER = 'HEAD-BACKOFFICE';
export type KnowledgeDocType = (typeof KNOWLEDGE_DOC_TYPES)[number];

export type KnowledgeVersionState =
  'in_review' | 'approved' | 'rejected' | 'published' | 'superseded' | 'revoked';

/**
 * Separa o cabeçalho (entre as linhas "---") do texto. Aceita "chave: valor" e listas no formato
 * [a, b]. Devolve null se o arquivo não começa com cabeçalho.
 */
export function parseFrontMatter(
  text: string,
): { data: Record<string, string | string[]>; body: string } | null {
  const src = text.replace(/\r\n/g, '\n');
  if (!src.startsWith('---\n')) return null;
  const end = src.indexOf('\n---\n', 3);
  if (end < 0) return null;
  const data: Record<string, string | string[]> = {};
  for (const line of src.slice(4, end).split('\n')) {
    const i = line.indexOf(':');
    if (i <= 0) continue;
    const key = line.slice(0, i).trim();
    const raw = line.slice(i + 1).trim();
    data[key] =
      raw.startsWith('[') && raw.endsWith(']')
        ? raw
            .slice(1, -1)
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean)
        : raw;
  }
  return { data, body: src.slice(end + 5) };
}

const roleCode = z.string().regex(/^[A-Z0-9][A-Z0-9_-]{1,29}$/, 'Código de perfil inválido');

/** Cabeçalho obrigatório dos documentos (padrão em docs/conhecimento/README.md). */
export const knowledgeFrontMatter = z
  .object({
    codigo: z.string().regex(/^[A-Z][A-Z0-9-]{2,39}$/, 'Código inválido'),
    titulo: z.string().min(1).max(300),
    tipo: z.enum(KNOWLEDGE_DOC_TYPES),
    departamento: z.enum(KNOWLEDGE_DEPARTMENTS),
    modulos: z.array(z.string().min(1).max(60)).default([]),
    processo: z.string().max(200).optional(),
    responsavel_conteudo: z.string().min(1).max(200),
    aprovador_perfil: roleCode,
    revisores: z.array(roleCode).default([]),
    confidencialidade: z.enum(['interno', 'restrito']),
    versao: z.string().regex(/^[0-9]+(\.[0-9]+){1,2}$/, 'Versão no formato 1.0 ou 1.0.0'),
    status: z.enum(['rascunho', 'obsoleto']),
    revisado_em: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    palavras_chave: z.array(z.string().min(1).max(80)).default([]),
    relacionados: z.array(z.string().regex(/^[A-Z][A-Z0-9-]{2,39}$/)).default([]),
    /** E-mail de quem escreveu ou pediu esta versão: essa pessoa não pode aprová-la. */
    autor: z.email().optional(),
    /** Origem quando não há autoria individual (ex.: documentação inicial elaborada com apoio de IA). */
    origem: z.string().min(1).max(300).optional(),
    /**
     * Só em novidades de melhorias já instaladas antes deste campo existir: data e versão reais da
     * instalação, tiradas do histórico do workflow "Instalar no servidor".
     */
    disponivel_em: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .refine((v) => v <= new Date().toISOString().slice(0, 10), 'Data no futuro')
      .optional(),
    versao_instalada: z
      .string()
      .regex(/^[0-9a-f]{7,40}$/, 'Use o código da versão instalada (commit)')
      .optional(),
  })
  .refine((v) => v.tipo === 'novidade' || (v.disponivel_em == null && v.versao_instalada == null), {
    path: ['disponivel_em'],
    message: 'Data e versão de instalação só em novidades',
  })
  .refine((v) => (v.disponivel_em == null) === (v.versao_instalada == null), {
    path: ['versao_instalada'],
    message: 'Informe a data e a versão da instalação juntas',
  })
  .refine(
    (v) =>
      (v.tipo !== 'faq' && v.tipo !== 'novidade') ||
      v.aprovador_perfil === KNOWLEDGE_CENTRAL_APPROVER,
    {
      path: ['aprovador_perfil'],
      message: 'Perguntas frequentes e novidades são aprovadas pelo HEAD-BACKOFFICE',
    },
  )
  .refine(
    (v) =>
      v.confidencialidade === 'interno' ||
      (KNOWLEDGE_RESTRICTED_DEPARTMENTS as readonly string[]).includes(v.departamento),
    { path: ['confidencialidade'], message: 'Restrito só para finance, legal, hr e personnel' },
  );
export type KnowledgeFrontMatter = z.infer<typeof knowledgeFrontMatter>;

// ----- Entradas da API -----

export const knowledgeReviewInput = z
  .object({
    roleCode,
    verdict: z.enum(['ok', 'changes']),
    comment: z
      .string()
      .trim()
      .max(2000)
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .optional(),
  })
  .refine((v) => v.verdict === 'ok' || !!v.comment, {
    path: ['comment'],
    message: 'Explique os ajustes necessários',
  });

export const knowledgeDecisionInput = z.object({ comment: z.string().trim().min(1).max(2000) });
export const knowledgePublishInput = z.object({
  comment: z
    .string()
    .trim()
    .max(2000)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
});

export const knowledgeSearchQuery = z.object({
  q: z.string().trim().max(200).optional(),
  department: z.enum(KNOWLEDGE_DEPARTMENTS).optional(),
  type: z.enum(KNOWLEDGE_DOC_TYPES).optional(),
  module: z.string().trim().max(60).optional(),
});
